<script setup>
import { usePageHead } from '@composables/use-page-head'
import { ref } from 'vue'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import Layout from '@layouts/login'
import SocialLogin from '@views/auth/social-login'
import { email as emailValidator, required, useFormValidation } from '@composables/use-form-validation'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const email = ref('')
const password = ref('')
const checking = ref(false)
const validation = useFormValidation({
    email: { value: email, validators: [required, emailValidator] },
    password: { value: password, validators: [required] },
})

function notify(messageKey, type) {
    appearanceStore.handleNotification({
        message: getMessageText(messageKey),
        type,
    })
}

function clearFields() {
    email.value = ''
    password.value = ''
    validation.$reset()
}

function handleSuccess() {
    checking.value = false
    postAuthRedirect.resolveAfterAuth(localSave.get('redirectURL') || '/')
}

function handleError(error) {
    clearFields()
    checking.value = false
    if (error.response.status === 404) {
        notify('EMAIL_NOT_FOUND', 'danger')
        return
    }
    notify(error.response.status === 401 ? 'WRONG_PASSWORD' : 'SOMETHING_WENT_WRONG', 'danger')
}

function tryToLogIn() {
    checking.value = true
    if (validation.$invalid) {
        notify('EMAIL_PASSWORD_REQUIRED', 'danger')
        validation.$reset()
        checking.value = false
        return undefined
    }

    return authStore
        .logIn({ email: email.value, password: password.value })
        .then(handleSuccess)
        .catch(handleError)
}

usePageHead({ title: 'Log in', description: 'Log in' })
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div class="login-form-container">
                    <h4 class="login-title">Log In</h4>
                    <SocialLogin />
                    <div class="login-line-separator">OR</div>
                    <form
                        class="login-form"
                        @submit.prevent="tryToLogIn"
                    >
                        <div class="login-field-container">
                            <BaseInput
                                v-model="email"
                                :class="['login-field', validation.email.$error && 'login-error']"
                                name="email"
                                autocomplete="current-email"
                                placeholder="email*"
                                @blur="validation.email.$touch()"
                            />
                            <label
                                v-if="validation.email.$error"
                                :class="['login-label', 'login-error']"
                            >This field is required and must be an email</label>
                        </div>
                        <div class="login-field-container">
                            <BaseInput
                                v-model="password"
                                :class="['login-field', validation.password.$error && 'login-error']"
                                name="password"
                                type="password"
                                autocomplete="current-password"
                                placeholder="password*"
                                @blur="validation.password.$touch()"
                            />
                            <label
                                v-if="validation.password.$error"
                                :class="['login-label', 'login-error']"
                            >This field is required</label>
                        </div>
                        <div class="login-button-container">
                            <BaseButton
                                class="login-button"
                                :disabled="checking"
                                variant="primary"
                                fill
                                type="submit"
                                @click="() => {}"
                            >Log In</BaseButton>
                        </div>
                        <div class="login-forgot-password">
                            <div>Forgot your password?</div>
                            <BaseLink
                                class="login-forgot-password-link"
                                :to="{ name: 'forgot-password' }"
                            >Click here to reset your password</BaseLink>
                        </div>
                        <div class="login-agreement">
                            If you log in using Apple, Google, or Facebook and are not already registered,
                            a new account will be created and you will agree to our
                            <a
                                class="login-link"
                                href="https://frisky.fm/about/terms"
                                target="_blank"
                            >Terms and Conditions</a>
                            &
                            <a
                                class="login-link"
                                href="https://frisky.fm/about/privacy-policy"
                                target="_blank"
                            >Privacy Policy</a>.
                        </div>
                        <div class="login-additional-options">
                            <span class="login-additional-text">Don't have an account?</span>
                            <BaseLink :to="{ name: 'signup' }">
                                <BaseButton
                                    variant="secondary"
                                    size="m"
                                    @click="() => {}"
                                >Sign Up</BaseButton>
                            </BaseLink>
                        </div>
                    </form>
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.login-form-container {
    position: relative;
    margin-bottom: $s-h;
    .login-field {
        &.login-error {
            margin-bottom: $s-xl;
            border-color: $c-status-error-text;
            border-width: 2px;
        }
    }
}
.login-field-container {
    position: relative;
    width: 100%;
}
.login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.login-label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.login-error {
        position: absolute;
        bottom: $s-xs;
        color: $c-status-error-text;
    }
}
.login-title {
    text-align: left;
}
.login-subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.login-line-separator {
    @include lineSeparator(true, $c-border-default);

    @extend %contentSubtitleS;

    margin-bottom: $s-m * 2;
    font-weight: 100;
}

.login-button-container {
    width: 100%;
    // The button no longer carries an implicit bottom margin, so the container
    // holds the gap to the "Forgot your password?" block below it.
    margin-bottom: $space-24;
}

.login-agreement {
    @extend %contentSubtitleS;

    margin-top: $s-l;
}

a.login-link {
    color: $c-status-error-text;
}

.login-additional {
    // The Sign Up pill hugs its label now, so it shares a row with the prompt at every
    // width; the gap is what keeps them apart (template whitespace between the two is
    // condensed away). Wraps onto two centred lines if a phone is too narrow for both.
    &-options {
        display: flex;
        flex-wrap: wrap;
        row-gap: $space-12;
        column-gap: $space-24;
        align-items: center;
        justify-content: center;
        margin-top: $s-h;
    }
    &-text {
        @extend %contentSubtitleM;
    }
}

.login-forgot-password {
    @extend %contentSubtitleL;

    color: $c-text-faded;
}
a.login-forgot-password-link {
    font-weight: 600;
    color: $c-highlight;
}
</style>
